import React, { Component } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Spinner, Table } from "reactstrap";
import Select from "react-select";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Bar } from "react-chartjs-2";
import showToast from "../../components/showToast";
import call_api from "../../components/api-caller";

class UserLoginActivity extends Component {
    constructor(props) {
        super(props);

        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);

        this.state = {
            loading: false,
            allUsers: [],
            selectedUser: null,
            startDate: thirtyDaysAgo,
            endDate: today,
            activityData: null,
            chartData: null,
        };

        this.fetchAllUsers = this.fetchAllUsers.bind(this);
        this.fetchDailyActivity = this.fetchDailyActivity.bind(this);
        this.handleUserChange = this.handleUserChange.bind(this);
    }

    componentDidMount() {
        this.fetchAllUsers();
    }

    async fetchAllUsers() {
        this.setState({ loading: true });
        const query = `query {
            getAllUsers(email:"${this.props.email}", token: "${this.props.token}", realm: "${this.props.realm}", domain: "${this.props.domain}") {
                name
                email
            }
        }`;

        try {
            const data = await call_api(query);
            if (data && data.data && data.data.getAllUsers && data.data.getAllUsers.length >= 0) {
                const sortedUsers = data.data.getAllUsers.sort((a, b) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

                this.setState({
                    allUsers: sortedUsers,
                    loading: false,
                });
            } else {
                showToast("Error", "Failed to fetch users", "error");
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            showToast("Error", "An unexpected error occurred", "error");
            this.setState({ loading: false });
        }
    }

    async fetchDailyActivity() {
        const { selectedUser, startDate, endDate } = this.state;

        if (!selectedUser) {
            showToast("Error", "Please select a user", "error");
            return;
        }

        if (!startDate || !endDate) {
            showToast("Error", "Please select both start and end dates", "error");
            return;
        }

        this.setState({ loading: true });

        const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
        const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

        const query = `query {
            getUserDailyActivity(
                email: "${this.props.email}",
                token: "${this.props.token}",
                realm: "${this.props.realm}",
                domain: "${this.props.domain}",
                userEmail: "${selectedUser.email}",
                startDate: "${formattedStartDate}",
                endDate: "${formattedEndDate}"
            ) {
                userEmail
                totalLoginMinutes
                totalAppUsageMinutes
                entries {
                    date
                    loginTimeMinutes
                    appUsageTimeMinutes
                    sessionCount
                }
            }
        }`;

        try {
            const data = await call_api(query);

            if (data && data.data && data.data.getUserDailyActivity) {
                const activity = data.data.getUserDailyActivity;
                const entries = activity.entries || [];

                const chartLabels = entries.map(entry =>
                    moment(entry.date).format('MMM DD')
                );

                const chartData = {
                    labels: chartLabels,
                    datasets: [
                        {
                            label: 'Login Time (min)',
                            data: entries.map(entry => entry.loginTimeMinutes),
                            backgroundColor: 'rgba(54, 162, 235, 0.7)',
                            borderColor: 'rgba(54, 162, 235, 1)',
                            borderWidth: 1,
                        },
                        {
                            label: 'App Usage (min)',
                            data: entries.map(entry => entry.appUsageTimeMinutes),
                            backgroundColor: 'rgba(75, 192, 192, 0.7)',
                            borderColor: 'rgba(75, 192, 192, 1)',
                            borderWidth: 1,
                        }
                    ]
                };

                this.setState({
                    activityData: activity,
                    chartData: chartData,
                    loading: false,
                });
            } else {
                showToast("Error", "Failed to fetch daily activity", "error");
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error("Error fetching daily activity:", error);
            showToast("Error", "An unexpected error occurred", "error");
            this.setState({ loading: false });
        }
    }

    handleUserChange(selectedOption) {
        this.setState({ selectedUser: selectedOption });
    }

    formatDuration(minutes) {
        if (minutes === null || minutes === undefined) return "N/A";
        const hrs = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        if (hrs > 0) {
            return `${hrs}h ${mins}m`;
        }
        return `${mins}m`;
    }

    render() {
        const { loading, allUsers, selectedUser, startDate, endDate, activityData, chartData } = this.state;

        const userOptions = allUsers.map(user => ({
            value: user.email,
            label: `${user.name} (${user.email})`,
            email: user.email,
            name: user.name
        }));

        const chartOptions = {
            scales: {
                yAxes: [{
                    ticks: {
                        beginAtZero: true,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: 'Minutes',
                    }
                }],
                xAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: 'Date',
                    }
                }]
            },
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: true,
                position: 'top',
            },
        };

        return (
            <React.Fragment>
                <h4 className="card-title mb-4">User Daily Activity</h4>

                <Row className="mb-4 align-items-end">
                    <Col md={4} className="mb-2">
                        <div className="form-group mb-0">
                            <label>Select User</label>
                            <Select
                                value={selectedUser}
                                onChange={this.handleUserChange}
                                options={userOptions}
                                className="select2-selection"
                                placeholder="Select User"
                            />
                        </div>
                    </Col>
                    <Col md={3} className="mb-2">
                        <div className="form-group mb-0">
                            <label>Start Date</label>
                            <DatePicker
                                selected={startDate}
                                onChange={date => this.setState({ startDate: date })}
                                className="form-control"
                                dateFormat="yyyy-MM-dd"
                                placeholderText="Start date"
                                maxDate={endDate || new Date()}
                            />
                        </div>
                    </Col>
                    <Col md={3} className="mb-2">
                        <div className="form-group mb-0">
                            <label>End Date</label>
                            <DatePicker
                                selected={endDate}
                                onChange={date => this.setState({ endDate: date })}
                                className="form-control"
                                dateFormat="yyyy-MM-dd"
                                placeholderText="End date"
                                minDate={startDate}
                                maxDate={new Date()}
                            />
                        </div>
                    </Col>
                    <Col md={2} className="mb-2">
                        <Button
                            color="primary"
                            className="btn btn-primary w-100"
                            onClick={this.fetchDailyActivity}
                            disabled={loading || !selectedUser}
                        >
                            {loading ? <Spinner size="sm" /> : "Fetch"}
                        </Button>
                    </Col>
                </Row>

                {activityData && (
                    <React.Fragment>
                        <Row className="mb-4">
                            <Col md={4}>
                                <Card className="mini-stats-wid">
                                    <CardBody className="text-center">
                                        <h5 className="text-muted mb-2">Total Login Time</h5>
                                        <h3>{this.formatDuration(activityData.totalLoginMinutes)}</h3>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="mini-stats-wid">
                                    <CardBody className="text-center">
                                        <h5 className="text-muted mb-2">Total App Usage</h5>
                                        <h3>{this.formatDuration(activityData.totalAppUsageMinutes)}</h3>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="mini-stats-wid">
                                    <CardBody className="text-center">
                                        <h5 className="text-muted mb-2">Days Active</h5>
                                        <h3>{activityData.entries ? activityData.entries.filter(e => e.sessionCount > 0).length : 0}</h3>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Card>
                                    <CardBody>
                                        <CardTitle className="mb-4">Daily Login & App Usage</CardTitle>
                                        <div style={{ height: '350px' }}>
                                            {chartData && chartData.labels.length > 0 ? (
                                                <Bar data={chartData} options={chartOptions} />
                                            ) : (
                                                <div className="text-center p-5">
                                                    <p className="text-muted">No activity data for the selected period</p>
                                                </div>
                                            )}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Card>
                                    <CardBody>
                                        <CardTitle className="mb-4">Daily Breakdown</CardTitle>
                                        <div className="table-responsive">
                                            <Table className="table-centered table-nowrap mb-0 table-striped">
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Login Time</th>
                                                        <th>App Usage</th>
                                                        <th>Sessions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {activityData.entries && activityData.entries.length > 0 ? (
                                                        activityData.entries.map((entry, index) => (
                                                            <tr key={index}>
                                                                <td>{moment(entry.date).format('ddd, MMM DD YYYY')}</td>
                                                                <td>{this.formatDuration(entry.loginTimeMinutes)}</td>
                                                                <td>{this.formatDuration(entry.appUsageTimeMinutes)}</td>
                                                                <td>{entry.sessionCount}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4" className="text-center">No activity data available</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    }
}

export default UserLoginActivity;